import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, lastValueFrom, Observable, Subscription } from 'rxjs';
import { MachineDashboardDto, MachineDto } from 'src/app/shared/dto/machine.dto';
import { MachineDashboardResponseDto, MachineResponseDto } from 'src/app/shared/dto/pagination/machine.dto';
import { SensorValueDto } from 'src/app/shared/dto/sensor-value.dto';
import { MachineStatesEnum } from 'src/app/shared/enums/machine-states.enum';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})

export class MachineService extends BaseService {

    public machines$: Observable<MachineDto[]>;
    public machinesDashboard$: Observable<MachineDashboardDto[]>;

    private machinesSubject = new BehaviorSubject<MachineDto[]>([]);
    private messageMachinesSubscription: Subscription | undefined;
    private messageProcessessSubscription: Subscription | undefined;

    private machinesDashboardSubject = new BehaviorSubject<MachineDashboardDto[]>([]);
    private messageDashboardMachinesSubscription: Subscription | undefined;
    private messageDashboardProcessessSubscription: Subscription | undefined;

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'machines');

        this.machines$ = this.machinesSubject.asObservable();
        this.machinesDashboard$ = this.machinesDashboardSubject.asObservable();
    }

    async setupWebSocket(): Promise<void> {
        try {
            const response = await this.findAll();
            this.machinesSubject.next(response.machines);

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.MACHINES_ROOM));

            this.messageMachinesSubscription = this.socketService.getNotifications(Messages.MACHINES_CHANGED).subscribe(async () => {
                const response = await this.findAll();
                this.machinesSubject.next(response.machines);
            });

            this.messageProcessessSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDERS_CHANGED).subscribe(async () => {
                const response = await this.findAll();
                this.machinesSubject.next(response.machines);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Máquinas. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        if (this.messageMachinesSubscription) {
            this.messageMachinesSubscription.unsubscribe();
        }

        if (this.messageProcessessSubscription) {
            this.messageProcessessSubscription.unsubscribe();
        }

        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.MACHINES_ROOM));
    }

    async setupWebSocketDashboard(): Promise<void> {
        try {
            const responseDashboard = await this.findAllDashboard();
            this.machinesDashboardSubject.next(responseDashboard.machines);

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.MACHINES_DASHBOARD_ROOM));

            this.messageDashboardMachinesSubscription = this.socketService.getNotifications(Messages.MACHINES_CHANGED).subscribe(async () => {
                const responseDashboard = await this.findAllDashboard();
                this.machinesDashboardSubject.next(responseDashboard.machines);
            });

            this.messageDashboardProcessessSubscription = this.socketService.getNotifications(Messages.PRODUCTION_ORDERS_CHANGED).subscribe(async () => {
                const responseDashboard = await this.findAllDashboard();
                this.machinesDashboardSubject.next(responseDashboard.machines);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Máquinas dashboard. Error: ${e.message}`);
        }
    }

    disconnectWebSocketDashboard(): void {
        if (this.messageDashboardMachinesSubscription) {
            this.messageDashboardMachinesSubscription.unsubscribe();
        }

        if (this.messageDashboardProcessessSubscription) {
            this.messageDashboardProcessessSubscription.unsubscribe();
        }

        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.MACHINES_DASHBOARD_ROOM));
    }

    async getMachineAvailable(ids: number[]): Promise<MachineDto[]> {
        try {
            if (ids.length === 0) {
                throw new Error('La lista de ids de máquinas no puede estar vacía');
            }

            let params = new HttpParams().set('conditionType', 'NOT');

            for (let i = 0; i < ids.length; i++) {
                params = params.append('ids', ids[i]);
            }

            const request = this.httpClient.get<MachineDto[]>(this.url, { params: params });

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener las máquinas disponibles. Error: ${e.message}`);
        }
    }

    async findAll(): Promise<MachineResponseDto> {
        try {
            const request = this.httpClient.get<MachineResponseDto>(`${this.url}/findAll`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ninguna máquina. Error: ${e.message}`);
        }
    }

    async findAllDashboard(): Promise<MachineDashboardResponseDto> {
        try {
            const request = this.httpClient.get<MachineDashboardResponseDto>(`${this.url}/findAllDashboard`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ninguna máquina. Error: ${e.message}`);
        }
    }

    async create(machine: MachineDto): Promise<MachineDto> {
        try {
            if (machine === null || machine === undefined) {
                throw new Error('La máquina no es válida');
            }

            const request = this.httpClient.post<MachineDto>(`${this.url}`, machine);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido crear la máquina. Error: ${e.message}`);
        }
    }

    async updateMachine(machine: MachineDto): Promise<MachineDto> {
        try {
            if (machine === null || machine === undefined) {
                throw new Error('La máquina no es válida');
            }

            const request = this.httpClient.patch<MachineDto>(`${this.url}/${machine.id}`, machine);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar la máquina. Error: ${e.message}`);
        }
    }

    async deleteMachine(id: number): Promise<MachineDto> {
        try {
            if (id <= 0) {
                throw new Error('El id de la máquina ha de ser mayor de 0');
            }

            const request = this.httpClient.delete<MachineDto>(`${this.url}/${id}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido eliminar la máquina. Error: ${e.message}`);
        }
    }

    async updateMachineState(id: number, newMachineState: MachineStatesEnum): Promise<boolean> {
        try {
            if (id <= 0) {
                throw new Error('El id de la máquina ha de ser mayor de 0');
            } else if (newMachineState === null || newMachineState === undefined) {
                throw new Error('El estado de la máquina no es válido');
            }

            const request = this.httpClient.patch<boolean>(`${this.url}/updateMachineState/${id}`, { state: newMachineState });

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar el estado de la máquina. Error: ${e.message}`);
        }


    }

    //TODO NESTED PROCESSES: check if this function is needed
    async checkForUnassignedMachineSensorValues(machineId: number): Promise<SensorValueDto[]> {
        try {
            const result = this.httpClient.get<SensorValueDto[]>(`${this.url}/${machineId}/unassigned-sensor-values`);
            return await firstValueFrom(result);
        } catch (error: any) {
            throw new Error(`No se ha podido recuperar los valores de los sensores sin asignar de la máquina ${machineId}.`);
        }
    }
}
